import { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle, XCircle, AlertCircle, Send, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/environment.js';
import { authFetch } from '../utils/authFetch';

/**
 * SMS Opt-In Component
 * Handles SMS consent workflow with TCPA/CTIA compliance
 */

import { getUserId } from '../utils/getUserId';

// ── Constants ────────────────────────────────────────────────────────

const INITIAL_OPT_IN_STATE = {
  requesting: false,
  confirming: false,
  optingOut: false,
  showConfirmDialog: false,
  confirmationCode: '',
  agreedToTerms: false
};

const STATUS_BADGE_CONFIG = {
  opted_in: {
    bg: '#f0fdf4',
    border: '#86efac',
    color: '#16a34a',
    label: 'SMS Notifications Active',
    Icon: CheckCircle,
    iconColor: '#22c55e'
  },
  pending: {
    bg: '#fef9c3',
    border: '#fde047',
    color: '#ca8a04',
    label: 'Confirmation Pending',
    Icon: AlertCircle,
    iconColor: '#eab308'
  },
  opted_out: {
    bg: '#fef2f2',
    border: '#fecaca',
    color: '#dc2626',
    label: 'SMS Notifications Disabled',
    Icon: XCircle,
    iconColor: '#ef4444'
  },
  not_requested: {
    bg: '#f3f4f6',
    border: '#d1d5db',
    color: '#6b7280',
    label: 'SMS Notifications Not Enabled',
    Icon: MessageSquare,
    iconColor: '#6b7280'
  }
};

// ── Helper functions ─────────────────────────────────────────────────

// ── Helper functions ─────────────────────────────────────────────────

const sanitizeCodeInput = (value) =>
  value.replace(/\D/g, '').slice(0, 6);

// ── Sub-components ───────────────────────────────────────────────────

const LoadingState = () => (
  <div style={{
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    textAlign: 'center'
  }}>
    <p style={{ color: '#6b7280', margin: 0 }}>Loading SMS status...</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const config = STATUS_BADGE_CONFIG[status] || STATUS_BADGE_CONFIG.not_requested;
  const { Icon } = config;

  return (
    <div style={{
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '16px',
      backgroundColor: config.bg,
      border: `1px solid ${config.border}`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Icon size={18} style={{ color: config.iconColor }} />
        <span style={{
          fontSize: '14px',
          fontWeight: '600',
          color: config.color
        }}>
          {config.label}
        </span>
      </div>
    </div>
  );
};

const TermsAndConditions = ({ agreedToTerms, onToggle }) => (
  <div style={{
    padding: '16px',
    backgroundColor: '#f0f9ff',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #bae6fd'
  }}>
    <div style={{ display: 'flex', alignItems: 'start', gap: '8px', marginBottom: '12px' }}>
      <Shield size={18} style={{ color: '#0284c7', marginTop: '2px', flexShrink: 0 }} />
      <div>
        <p style={{ fontSize: '13px', fontWeight: '600', color: '#0369a1', margin: '0 0 8px 0' }}>
          SMS Terms & Conditions
        </p>
        <p style={{ fontSize: '12px', color: '#075985', margin: '0 0 8px 0', lineHeight: '1.5' }}>
          By opting in, you agree to receive SMS notifications from TravelFlow about your flight bookings, 
          including gate changes, delays, and departure reminders. Message and data rates may apply. 
          Message frequency varies based on your trips. Reply STOP to opt out or HELP for assistance.
        </p>
        <p style={{ fontSize: '12px', color: '#075985', margin: '0', lineHeight: '1.5' }}>
          Your phone number will be stored securely and used only for TravelFlow notifications. 
          View our <a href="#" style={{ color: '#0284c7', textDecoration: 'underline' }}>Privacy Policy</a> and{' '}
          <a href="#" style={{ color: '#0284c7', textDecoration: 'underline' }}>Terms of Service</a>.
        </p>
      </div>
    </div>

    <label style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '13px',
      color: '#0369a1',
      cursor: 'pointer'
    }}>
      <input
        type="checkbox"
        checked={agreedToTerms}
        onChange={(e) => onToggle(e.target.checked)}
        style={{ width: '16px', height: '16px', accentColor: '#0284c7' }}
      />
      I agree to receive SMS notifications and accept the terms
    </label>
  </div>
);

const NotRequestedState = ({ phoneNumber, optInState, onAgreeToggle, onRequestOptIn }) => {
  const canSubmit = optInState.agreedToTerms && phoneNumber;

  return (
    <div>
      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
        Enable SMS notifications to receive important flight updates via text message.
      </p>

      <TermsAndConditions
        agreedToTerms={optInState.agreedToTerms}
        onToggle={onAgreeToggle}
      />

      <button
        onClick={onRequestOptIn}
        disabled={optInState.requesting || !canSubmit}
        style={{
          width: '100%',
          padding: '12px 16px',
          backgroundColor: canSubmit ? '#3b82f6' : '#9ca3af',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: optInState.requesting || !canSubmit ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <Send size={16} />
        {optInState.requesting ? 'Sending Confirmation...' : 'Enable SMS Notifications'}
      </button>

      {!phoneNumber && (
        <p style={{
          fontSize: '12px',
          color: '#dc2626',
          margin: '8px 0 0 0',
          textAlign: 'center'
        }}>
          Please save your phone number before enabling SMS
        </p>
      )}
    </div>
  );
};

const PendingState = ({ phoneNumber, optInState, onConfirm, onCancel, onCodeChange, onResend }) => (
  <div>
    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
      Enter the 6-digit confirmation code sent to {phoneNumber}
    </p>

    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
      <input
        type="text"
        placeholder="Enter 6-digit code"
        value={optInState.confirmationCode}
        onChange={(e) => onCodeChange(sanitizeCodeInput(e.target.value))}
        maxLength={6}
        style={{
          flex: 1,
          padding: '12px',
          border: '1px solid #cbd5e1',
          borderRadius: '6px',
          fontSize: '18px',
          letterSpacing: '4px',
          textAlign: 'center',
          fontWeight: '600'
        }}
      />
    </div>

    <div style={{ display: 'flex', gap: '8px' }}>
      <button
        onClick={onConfirm}
        disabled={optInState.confirming || optInState.confirmationCode.length !== 6}
        style={{
          flex: 1,
          padding: '10px 16px',
          backgroundColor: '#22c55e',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: optInState.confirming ? 'not-allowed' : 'pointer',
          opacity: optInState.confirming ? 0.7 : 1
        }}
      >
        {optInState.confirming ? 'Verifying...' : 'Confirm'}
      </button>

      <button
        onClick={onCancel}
        style={{
          padding: '10px 16px',
          backgroundColor: '#e5e7eb',
          color: '#374151',
          border: 'none',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer'
        }}
      >
        Cancel
      </button>
    </div>

    <button
      onClick={onResend}
      disabled={optInState.requesting}
      style={{
        width: '100%',
        marginTop: '12px',
        padding: '8px',
        backgroundColor: 'transparent',
        color: '#3b82f6',
        border: 'none',
        fontSize: '12px',
        textDecoration: 'underline',
        cursor: 'pointer'
      }}
    >
      Resend code
    </button>
  </div>
);

const OptedInState = ({ phoneNumber, optingOut, onOptOut }) => (
  <div>
    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
      You're receiving SMS notifications at {phoneNumber}
    </p>

    <button
      onClick={onOptOut}
      disabled={optingOut}
      style={{
        width: '100%',
        padding: '10px 16px',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: optingOut ? 'not-allowed' : 'pointer',
        opacity: optingOut ? 0.7 : 1
      }}
    >
      {optingOut ? 'Processing...' : 'Disable SMS Notifications'}
    </button>

    <p style={{
      fontSize: '11px',
      color: '#9ca3af',
      margin: '8px 0 0 0',
      textAlign: 'center'
    }}>
      You can also text STOP to opt out anytime
    </p>
  </div>
);

const OptedOutState = ({ onReEnable }) => (
  <div>
    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
      SMS notifications are currently disabled. You can re-enable them anytime.
    </p>

    <button
      onClick={onReEnable}
      style={{
        width: '100%',
        padding: '12px 16px',
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer'
      }}
    >
      Re-enable SMS Notifications
    </button>
  </div>
);

// ── Main component ───────────────────────────────────────────────────

const SMSOptIn = ({ user, userAttributes, phoneNumber, onStatusChange }) => {
  const [consentStatus, setConsentStatus] = useState({
    status: 'not_requested',
    hasConsent: false,
    loading: true
  });

  const [optInState, setOptInState] = useState(INITIAL_OPT_IN_STATE);

  const userId = getUserId(user, userAttributes);

  const loadConsentStatus = async () => {
    try {
      setConsentStatus(prev => ({ ...prev, loading: true }));

      const response = await authFetch(`${API_BASE_URL}/sms-optin/status?userId=${userId}`);
      const data = await response.json();

      if (response.ok && data.success) {
        setConsentStatus({
          status: data.status,
          hasConsent: data.hasConsent,
          loading: false,
          phoneNumber: data.phoneNumber,
          requestedAt: data.requestedAt,
          confirmedAt: data.confirmedAt
        });

        if (onStatusChange) {
          onStatusChange(data.hasConsent, data.status);
        }
      } else {
        setConsentStatus({ status: 'not_requested', hasConsent: false, loading: false });
      }
    } catch (error) {
      console.error('Error loading consent status:', error);
      setConsentStatus({ status: 'not_requested', hasConsent: false, loading: false });
    }
  };

  useEffect(() => {
    if (userId) {
      loadConsentStatus();
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestOptIn = async () => {
    if (!phoneNumber || phoneNumber.trim() === '') {
      toast.error('Please save your phone number first');
      return;
    }

    if (!optInState.agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    setOptInState(prev => ({ ...prev, requesting: true }));

    try {
      const response = await authFetch(`${API_BASE_URL}/sms-optin/request-optin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          phoneNumber,
          email: userAttributes?.email
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Confirmation code sent to your phone!');
        setOptInState(prev => ({
          ...prev,
          requesting: false,
          showConfirmDialog: true
        }));
        setConsentStatus(prev => ({ ...prev, status: 'pending' }));
      } else {
        toast.error(data.error || 'Failed to send confirmation code');
        setOptInState(prev => ({ ...prev, requesting: false }));
      }
    } catch (error) {
      console.error('Error requesting opt-in:', error);
      toast.error('Failed to send confirmation code');
      setOptInState(prev => ({ ...prev, requesting: false }));
    }
  };

  const confirmOptIn = async () => {
    if (!optInState.confirmationCode || optInState.confirmationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setOptInState(prev => ({ ...prev, confirming: true }));

    try {
      const response = await authFetch(`${API_BASE_URL}/sms-optin/confirm-optin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          phoneNumber,
          confirmationCode: optInState.confirmationCode
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Successfully opted in to SMS notifications!');
        setOptInState(INITIAL_OPT_IN_STATE);
        await loadConsentStatus();
      } else {
        toast.error(data.error || 'Invalid confirmation code');
        setOptInState(prev => ({ ...prev, confirming: false }));
      }
    } catch (error) {
      console.error('Error confirming opt-in:', error);
      toast.error('Failed to confirm opt-in');
      setOptInState(prev => ({ ...prev, confirming: false }));
    }
  };

  const handleOptOut = async () => {
    const confirmed = window.confirm('Are you sure you want to stop receiving SMS notifications? You can opt back in anytime.');
    if (!confirmed) return;

    setOptInState(prev => ({ ...prev, optingOut: true }));

    try {
      const response = await authFetch(`${API_BASE_URL}/sms-optin/opt-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          phoneNumber,
          reason: 'user_request_web'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Successfully opted out of SMS notifications');
        await loadConsentStatus();
      } else {
        toast.error(data.error || 'Failed to opt out');
      }
    } catch (error) {
      console.error('Error opting out:', error);
      toast.error('Failed to opt out');
    } finally {
      setOptInState(prev => ({ ...prev, optingOut: false }));
    }
  };

  const handleReEnable = () => {
    setConsentStatus(prev => ({ ...prev, status: 'not_requested' }));
    setOptInState(INITIAL_OPT_IN_STATE);
  };

  const handleCancelConfirm = () => {
    setOptInState(prev => ({ ...prev, showConfirmDialog: false, confirmationCode: '' }));
  };

  const handleCodeChange = (value) => {
    setOptInState(prev => ({ ...prev, confirmationCode: value }));
  };

  const handleAgreeToggle = (checked) => {
    setOptInState(prev => ({ ...prev, agreedToTerms: checked }));
  };

  if (consentStatus.loading) {
    return <LoadingState />;
  }

  const renderStateContent = () => {
    switch (consentStatus.status) {
      case 'not_requested':
        return (
          <NotRequestedState
            phoneNumber={phoneNumber}
            optInState={optInState}
            onAgreeToggle={handleAgreeToggle}
            onRequestOptIn={requestOptIn}
          />
        );
      case 'pending':
        if (!optInState.showConfirmDialog) return null;
        return (
          <PendingState
            phoneNumber={phoneNumber}
            optInState={optInState}
            onConfirm={confirmOptIn}
            onCancel={handleCancelConfirm}
            onCodeChange={handleCodeChange}
            onResend={requestOptIn}
          />
        );
      case 'opted_in':
        return (
          <OptedInState
            phoneNumber={consentStatus.phoneNumber}
            optingOut={optInState.optingOut}
            onOptOut={handleOptOut}
          />
        );
      case 'opted_out':
        return <OptedOutState onReEnable={handleReEnable} />;
      default:
        return null;
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <MessageSquare size={24} style={{ color: '#3b82f6' }} />
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          margin: 0
        }}>
          SMS Notifications
        </h3>
      </div>

      <StatusBadge status={consentStatus.status} />

      {renderStateContent()}
    </div>
  );
};

export default SMSOptIn;
